import { ApiResponseError, type ApiClient } from "@shell/auth/apiClient";
import { exportListResponseSchema } from "../common/schemas";
import { officeFloorplanResponseSchema, type OfficeFloorplanView } from "../workspaces/types";
import {
    sageOfficeListResponseSchema,
    sageOfficeMutationResponseSchema,
    sageOfficeViewSchema,
    type SageOfficeUpsertPayload,
    type SageOfficeView,
} from "./types";

interface SearchParams {
    readonly q?: string;
    readonly includeDeleted?: boolean;
}

export async function searchSageOffices(
    client: ApiClient,
    params: SearchParams,
): Promise<readonly SageOfficeView[]> {
    const search = new URLSearchParams();
    if (params.q !== undefined && params.q.length > 0) search.set("q", params.q);
    if (params.includeDeleted === true) search.set("includeDeleted", "true");

    const path = search.size > 0 ? `/api/sage-offices?${search.toString()}` : "/api/sage-offices";
    const raw = await client.getJson<unknown>(path);
    return sageOfficeListResponseSchema.parse(raw).results;
}

export async function exportSageOffices(
    client: ApiClient,
    params: SearchParams,
): Promise<{ readonly results: readonly SageOfficeView[]; readonly truncated: boolean }> {
    const search = new URLSearchParams();
    if (params.q !== undefined && params.q.length > 0) search.set("q", params.q);
    if (params.includeDeleted === true) search.set("includeDeleted", "true");

    const path =
        search.size > 0
            ? `/api/exports/sage-offices?${search.toString()}`
            : "/api/exports/sage-offices";
    const raw = await client.getJson<unknown>(path);
    return exportListResponseSchema(sageOfficeViewSchema).parse(raw);
}

export async function createSageOffice(
    client: ApiClient,
    payload: SageOfficeUpsertPayload,
): Promise<SageOfficeView> {
    const response = await client.fetch("/api/sage-offices", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    return await readMutationResponse(response);
}

export async function updateSageOffice(
    client: ApiClient,
    payload: SageOfficeUpsertPayload,
): Promise<SageOfficeView> {
    const response = await client.fetch(`/api/sage-offices/${encodeURIComponent(payload.id)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            sageOfficeId: payload.id,
            name: payload.name,
            address: payload.address,
        }),
    });
    return await readMutationResponse(response);
}

export async function deleteSageOffice(client: ApiClient, id: string): Promise<void> {
    const response = await client.fetch(`/api/sage-offices/${encodeURIComponent(id)}`, {
        method: "DELETE",
    });
    if (!response.ok) {
        const body = await response.text();
        throw new ApiResponseError(response.status, response.statusText, body);
    }
}

export async function restoreSageOffice(client: ApiClient, id: string): Promise<SageOfficeView> {
    const response = await client.fetch(`/api/sage-offices/${encodeURIComponent(id)}/restore`, {
        method: "POST",
    });
    return await readMutationResponse(response);
}

/**
 * Aggregated floorplan for one office — workspaces + current occupants.
 * Wire shapes live in workspaces types (compose {@link WorkspaceView}).
 */
export async function getOfficeFloorplan(
    client: ApiClient,
    sageOfficeId: string,
): Promise<OfficeFloorplanView> {
    const raw = await client.getJson<unknown>(
        `/api/sage-offices/${encodeURIComponent(sageOfficeId)}/floorplan`,
    );
    return officeFloorplanResponseSchema.parse(raw);
}

async function readMutationResponse(response: Response): Promise<SageOfficeView> {
    if (!response.ok) {
        const body = await response.text();
        throw new ApiResponseError(response.status, response.statusText, body);
    }
    const raw = (await response.json()) as unknown;
    return sageOfficeMutationResponseSchema.parse(raw).sageOffice;
}
